import { useEffect, useRef, useState } from 'react';
import { cyclesForwardConfidence } from '../../../api/admin';

/**
 * Fetches forward-confidence for the (symbol, timeframe) pair at the
 * supplied effective settings. Debounces requests so a user dragging
 * a slider in the Tune panel doesn't hammer the backend on every
 * keystroke — wait `debounceMs` of quiet first, then request.
 *
 * The endpoint runs the full detection pipeline against historical
 * candles and is computationally noticeable (~1-2s for weekly,
 * longer for hourly), so we deliberately keep the cadence slow.
 *
 * `effectiveSettings` should be the merged shape the parent already
 * computes for the chart's preview — `{ low: {...}, high: {...} }` —
 * so the accuracy number reflects exactly what the user is seeing
 * on the chart right now, not what's been saved to SystemSettings
 * yet. This is the whole point: measure THIS tune before committing.
 *
 * Returns:
 *   - `data`: latest server response, or null while pending or after error
 *   - `loading`: in-flight request indicator
 *   - `error`: last error message, or null
 *   - `refetch()`: force-refresh now (skips debounce)
 */
const useForwardConfidence = ({
  symbol,
  timeframe,
  effectiveSettings,
  enabled = true,
  debounceMs = 800,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Bumping `refreshToken` triggers a fetch even when nothing else
  // changed — used by `refetch()` and could be wired to a manual
  // "recheck" button if we want one later.
  const [refreshToken, setRefreshToken] = useState(0);
  const refetch = () => setRefreshToken((t) => t + 1);

  // Track the request count so a stale completion (dragged a slider,
  // first fetch was slow, second fetch finished first) can't overwrite
  // the latest result. Standard race-protection idiom.
  const requestSeqRef = useRef(0);

  // Stringified settings as a stable dep — extracted out of the dep
  // array so eslint-react-hooks can statically check the rest. We
  // re-run on JSON-stringified settings rather than identity so a
  // re-render that produced an equal object doesn't trigger a refetch.
  const settingsKey = JSON.stringify(effectiveSettings || {});

  useEffect(() => {
    if (!enabled || !symbol || !timeframe) {
      setData(null);
      setError(null);
      // Reset loading too — without this, an in-flight request from
      // a previous effect run that gets discarded by the cleanup
      // below would leave the panel showing "measuring…" forever
      // when this run early-exits.
      setLoading(false);
      // Bump seq so any previously-fired request that completes
      // after we early-exit gets discarded by the seq check.
      requestSeqRef.current += 1;
      return undefined;
    }

    const handle = setTimeout(async () => {
      const seq = requestSeqRef.current + 1;
      requestSeqRef.current = seq;
      setLoading(true);
      setError(null);
      try {
        const result = await cyclesForwardConfidence({
          symbol,
          timeframe,
          // Send only the merged settings the chart is using right
          // now. The endpoint falls back to SystemSettings when this
          // is omitted, but for the Tune preview we want THESE values
          // exactly — undirty + dirty merged together as the parent
          // already computes for chart rendering.
          settings: effectiveSettings,
        });
        if (requestSeqRef.current !== seq) {
          // A newer request was kicked off between our send and our
          // receive, OR cleanup ran (deps changed / unmount). Drop
          // this result on the floor.
          return;
        }
        setData(result);
      } catch (err) {
        if (requestSeqRef.current !== seq) {return;}
        const message = err?.response?.data?.error || err?.message || 'Failed to measure accuracy';
        setError(message);
        setData(null);
      } finally {
        if (requestSeqRef.current === seq) {
          setLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      clearTimeout(handle);
      // Bump seq so any request that's already in-flight from THIS
      // effect run (timeout fired before deps changed) gets
      // discarded by the seq check on completion. Without this, a
      // dep change mid-request lets stale data land in state —
      // bugbot caught the original race guard was incomplete.
      requestSeqRef.current += 1;
    };
    // `effectiveSettings` itself is omitted on purpose — we depend on
    // `settingsKey` (its stringification) so identity changes that
    // produce equal JSON don't trigger refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    symbol,
    timeframe,
    enabled,
    debounceMs,
    refreshToken,
    settingsKey,
  ]);

  return { data, loading, error, refetch };
};

export default useForwardConfidence;
